import BookingModule from "@/store/modules/booking";
import {
  BookingStatus,
  IProductSlotState,
  IQuantifiedProductSlotState,
  isSeatedPssType,
  PSSType,
} from "@/types/db/product-slot-state";
import { computed } from "vue";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import {
  IBookingLabel,
  IBookingReceipt,
  IBookingTicket,
  ITicketProduct,
  LabelTypes,
  TicketTypes,
} from "@/interfaces/receipts";
import type { OrderJson } from "@smeetz/orders";
import {
  printLabel,
  printMiniTicket,
  printNativeTicket,
  printReceipt,
} from "@/printing/handlers/receipts";
import {
  CustomFieldType,
  ICustomFieldAnswer,
  PCTypes,
  QuestionType,
  SeatType,
} from "@/types/db/product-category";
import {
  getProductCategoriesByIds,
  getProductCategoriesByUuids,
  getProductCategoryById,
} from "@/dexie/product-category";
import {
  BaseOrderSession,
  IProductSlotBooking,
} from "@/types/db/product-slot-booking";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import { cloneDeep } from "lodash";
import { IAccountingEntry } from "@/types/db/accounting-entry";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { LogLevel, LogService } from "@/services/logging";
import CashFloatModule from "@/store/modules/cash-float";
import { formatDateTime, nowDB } from "../helpers/dates";
import { generateID } from "../helpers/app";
import {
  applyReceiptCounter,
  buildBaseReceiptInfo,
} from "../helpers/receipt-builder";
import { applyCountryCompliance } from "../helpers/receipt-compliance";
import { buildOrderReceiptInput } from "../helpers/receipt-input-builder";
import type { ReceiptInput } from "@smeetz/receipt";
import ReceiptModule from "@/store/modules/receipt";
import { scanOrderTicketsBulk } from "./order-scanning";
import dayjs from "dayjs";
import { postOrderPrint } from "@/api/booking/booking";
import {
  getOrderVoucherPrintCodes,
  IVoucherPrintCodeEntry,
  IVoucherPrintCodesResponse,
} from "@/api/orders/orders";
import NotificationService from "@/services/notification.service";
import {
  getBookingCustomFieldFile,
  getDeserializedAnswers,
  getDeserializedCustomFieldQuestions,
  getTicketCustomFieldFile,
} from "@/api/custom-fields/custom-fields";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { ITeam, ITeamSetting, TeamLevel } from "@/interfaces/teams/teams";
import Timeout from "await-timeout";
import PrinterConfigModule from "@/store/modules/printers-config";
import AppModule from "@/store/modules/app";
import { SharedCallbacks } from "../shared-resources";
import {
  getLocalPictureBase64,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import { getTicketQRCode } from "./product-slot-state";
import {
  OrderLine,
  OrderLinePublicType,
  TokenStatus,
} from "@smeetz/order-common";
import {
  IOfferApplication,
  IOfferApplicationDetails,
} from "@smeetz/offers-common";
import { useOrderStore } from "@smeetz/order-store";
import { storeToRefs } from "pinia";

/**
 * Print tickets natively from POS
 *
 * Flow:
 *  1. Ensure tokens are loaded (fetched from backend if not already present)
 *  2. Build a lookup map of order lines from orderSummary
 *  3. Filter ACTIVE tokens down to printable ones (sendTicket !== false, not Retail)
 *  4. Fetch voucher codes for GiftCard / MembershipVoucher lines (TODO: section 2.7)
 *  5. Process tokens in batches of 20 to reduce memory pressure
 *  6. For each token: look up its order line, fetch product category from Dexie,
 *     assemble an ITicketProduct with all print data (names, dates, QR code, etc.)
 *  7. Send the assembled ticket data to the appropriate printer/template
 *  8. Optionally auto-scan printed tickets if configured
 */
export async function printTicketsNative() {
  const orderStore = useOrderStore();
  const { selectedOrder } = storeToRefs(orderStore);
  const order = selectedOrder.value;
  if (!order) {
    throw new Error("No selected order");
  }

  // --- Printer validation ---
  const ticketType = PrinterConfigModule.ticketType;
  const selectedPrinter = PrinterConfigModule.ticketPrinter;
  if (ticketType !== TicketTypes.MiniTicket && !selectedPrinter) {
    LogService.info("Ticket Printing: No printer is selected");
    NotificationService.showCustomError(t("error.no-selected-ticket-printer"));
    return;
  }

  try {
    const groupInfo = await getGroupInfo();
    const isVHS = POSConfigModule.isVHS;

    // --- Step 1: Build order line lookup ---
    // orderSummary provides a consolidated view of all order lines across all sessions
    // (previous + current), with net quantities after cancellations.
    const activeTokens = (order.tokens ?? []).filter(
      (t) => t.status === TokenStatus.ACTIVE
    );

    const { selectedOrderSummary: orderSummary } = storeToRefs(orderStore);
    const orderLineById = new Map<string, OrderLine>();
    for (const line of orderSummary.value.orderLines) {
      orderLineById.set(line.id, line);
    }

    // --- Step 2: Filter to printable tokens ---
    // Each active token = one ticket to print. We exclude:
    // - Tokens for lines with sendTicket=false (automatic addons that inherit parent's token)
    // - Tokens for Retail lines (retail items don't get printed tickets)
    const printableTokens = activeTokens.filter((t) => {
      const line = orderLineById.get(t.orderLineId);
      return (
        line?.sendTicket !== false &&
        line?.itemType !== OrderLinePublicType.RETAIL
      );
    });

    if (printableTokens.length === 0) {
      LogService.info("Ticket Printing: No printable tokens found");
      return;
    }

    // --- Step 3: Voucher code fetch (GiftCard / MembershipVoucher) ---
    // Promocodes are created server-side by the async backend sync; the endpoint
    // answers `complete: false` until every voucher ticket has its code, so poll
    // a few times and abort printing (with a notification) if they never land —
    // a voucher ticket must not print without its code.
    const voucherTokens = printableTokens.filter((tk) => {
      const line = orderLineById.get(tk.orderLineId);
      return (
        line?.itemType === OrderLinePublicType.VOUCHER ||
        line?.itemType === OrderLinePublicType.MEMBERSHIP_VOUCHER
      );
    });
    let voucherCodesByTokenId: Map<string, IVoucherPrintCodeEntry> | undefined;
    if (voucherTokens.length > 0) {
      LogService.info(
        `Ticket Printing: Fetching voucher codes for order ${order.id}`
      );
      try {
        let codes: IVoucherPrintCodesResponse | undefined;
        for (let attempt = 0; attempt < 5; attempt++) {
          if (attempt > 0) {
            await Timeout.set(3000);
          }
          codes = await getOrderVoucherPrintCodes(order.id);
          if (codes.complete) {
            break;
          }
        }
        if (!codes?.complete) {
          throw new Error("Voucher codes not synced yet");
        }
        voucherCodesByTokenId = new Map(
          codes.lines
            .flatMap((line) => line.vouchers)
            .map((voucher) => [voucher.tokenId, voucher])
        );
      } catch (error) {
        LogService.info(
          `Ticket Printing: Error fetching voucher codes for order ${order.id}`,
          error,
          LogLevel.Error
        );
        NotificationService.showCustomError(t("error.booking-info-fetch"));
        return;
      }
    }

    // --- Step 4: Process in batches ---
    // Batching reduces memory load when printing many tickets (e.g. group bookings).
    // Each batch fetches its product categories from Dexie, assembles ticket data,
    // then sends to the printer with a 2s delay between batches to avoid overlapping.
    const batchSize = 20;
    const batchCount = Math.ceil(printableTokens.length / batchSize);

    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const startIndex = batchIndex * batchSize;
      const endIndex = Math.min(startIndex + batchSize, printableTokens.length);
      const batch = printableTokens.slice(startIndex, endIndex);

      // Translated field labels for the ticket template
      const bookingTickets: IBookingTicket = {
        tickets: [],
        groupInfo,
        bookingRef: order.reference,
        currency: order.orderSession?.currency || "CHF",
        fieldDiscount: t("discount.discounted-price"),
        fieldReference: t("orders.reference"),
        fieldTicket: t("dashboards.ticket"),
        fieldPaid: t("field.paid"),
        fieldFrom: t("field.date-from"),
        fieldTo: t("field.date-to"),
        fieldPrice: t("field.price"),
        fieldPrintDateTime: t("field.purchased-on"),
        fieldSeat: t("field.seat"),
        fieldVHSTicketTax: t("field.vhs-ticket-tax"),
        fieldVHSTicketTerms: t("field.vhs-ticket-terms"),
        fieldValidUntil: t("field.valid-until"),
      };

      // Fetch product categories from Dexie for this batch (deduplicated).
      // Order lines carry the category UUID in categoryId.
      const pcUuids = new Set<string>();
      for (const token of batch) {
        const line = orderLineById.get(token.orderLineId);
        if (line?.categoryId) pcUuids.add(String(line.categoryId));
      }
      const productCategories = await getProductCategoriesByUuids([...pcUuids]);

      // --- Step 5: Assemble ticket data for each token ---
      for (const token of batch) {
        const orderLine = orderLineById.get(token.orderLineId);
        if (!orderLine) continue;

        const pc = productCategories.find(
          (pc) => pc.uuid === orderLine.categoryId
        );
        const address = pc?.address || "";
        const isIdivTax = pc?.product.isIdivTax || 0;

        const startDate = orderLine.startDateTime
          ? new Date(orderLine.startDateTime)
          : null;
        const endDate = orderLine.endDateTime
          ? new Date(orderLine.endDateTime)
          : null;
        const multiDayTicket =
          startDate && endDate
            ? dayjs(endDate).diff(dayjs(startDate), "day") > 0
            : false;

        const isMembership = [
          OrderLinePublicType.MEMBERSHIP,
          OrderLinePublicType.MEMBERSHIP_VOUCHER,
          OrderLinePublicType.MEMBERSHIP_SEATED,
        ].includes(orderLine.itemType);

        const isGiftCard = orderLine.itemType === OrderLinePublicType.VOUCHER;

        // Memberships, gift cards, and VHS multi-day tickets show date only (no time)
        const onlyDates =
          isMembership || isGiftCard || (isVHS && multiDayTicket);
        let startDateTime = startDate
          ? formatDateTime(startDate.toISOString(), onlyDates)
          : "";
        let endDateTime = endDate
          ? formatDateTime(endDate.toISOString(), onlyDates)
          : "";

        // Voucher code + printed validity for gift cards / membership vouchers.
        // The backend resolves the campaign display dates (Locarno day-pass
        // overrides, gift-card validity — old L3-7640 logic) into the entry's
        // ticketStartDateTime / ticketEndDateTime, so no client-side campaign
        // logic is needed. Tickets are matched by their unique token id.
        let voucherCode = "";
        if (
          orderLine.itemType === OrderLinePublicType.MEMBERSHIP_VOUCHER ||
          orderLine.itemType === OrderLinePublicType.VOUCHER
        ) {
          const voucher = voucherCodesByTokenId?.get(token.id);
          voucherCode = voucher?.codeUnique || voucher?.code || "";
          if (voucher?.ticketStartDateTime) {
            startDateTime = formatDateTime(
              voucher.ticketStartDateTime,
              onlyDates
            );
          }
          if (voucher?.ticketEndDateTime) {
            endDateTime = formatDateTime(voucher.ticketEndDateTime, onlyDates);
          }
        }

        const seatLabel = orderLine.seats?.length
          ? orderLine.seats[0].seatLabel
          : "";

        // Map OrderLine + OrderToken → ITicketProduct (same shape used by all templates)
        const ticket: ITicketProduct = {
          categoryName: pc?.seatingParentCategoryName || orderLine.categoryName,
          subCategoryName: seatLabel ? pc?.name : "",
          productName: pc?.product.name || orderLine.categoryName,
          priceName: orderLine.priceName,
          startDateTime,
          endDateTime,
          printDateTime: formatDateTime(nowDB(), false),
          productCustomText:
            pc?.product?.productBrandings?.[0]?.customText || "",
          priceValue: (orderLine.overridePrice ?? orderLine.unitPrice).toFixed(
            2
          ),
          isIdivTax: isIdivTax || 0,
          address: address || "",
          qrcode: await getTicketQRCode(
            token.token,
            orderLine.itemType,
            voucherCode || undefined
          ),
          seat: seatLabel,
          voucherCode,
          isGiftCard,
          isMembership,
          multiDayTicket,
          productLogo:
            isVHS && pc
              ? (await getLocalPictureBase64(
                  SmeetzPictureEntityTypes.ProductLogo,
                  pc.product.id
                )) || ""
              : "",
          barCode: token.barcode || "",
        };
        bookingTickets.tickets.push(ticket);
      }

      LogService.info(
        `Ticket Printing: Printing tickets (order logic) for order ${order.reference}`,
        { params: bookingTickets }
      );

      // --- Step 6: Send to printer ---
      // Template selection: MiniTicket uses receipt printer, others use ticket printer.
      // Group logo is only fetched for mini tickets.
      if (ticketType === TicketTypes.MiniTicket) {
        let groupLogoBase64 = "";
        if (POSConfigModule.groupInfo?.brandingList?.[0]?.brandingPictureList) {
          const groupLogoInfo =
            POSConfigModule.groupInfo.brandingList[0].brandingPictureList.find(
              (b) => b && b.type === SmeetzPictureEntityTypes.Group
            );
          if (groupLogoInfo) {
            groupLogoBase64 =
              (await getLocalPictureBase64(
                SmeetzPictureEntityTypes.Group,
                Number(groupLogoInfo.Id)
              )) || "";
            if (groupLogoBase64) {
              bookingTickets.groupLogoBase64 = groupLogoBase64;
            }
          }
        }
        printMiniTicket(bookingTickets);
      } else if (selectedPrinter) {
        bookingTickets.fieldReference = bookingTickets.fieldReference.substring(
          0,
          3
        );
        await printNativeTicket(bookingTickets, selectedPrinter);
      }

      // Delay between batches to avoid overlapping print jobs
      await Timeout.set(2000);
    }

    NotificationService.showCustomSuccess(
      t("completeorder.print-tickets-success")
    );

    // --- Step 7: Auto-scan if configured ---
    if (POSConfigModule.autoScanTickets) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await scanOrderTicketsBulk();
    }
  } catch (error) {
    LogService.info(
      "Ticket Printing: Error when printing tickets (Order logic)",
      error,
      LogLevel.Error
    );
    NotificationService.showCustomError(t("error.ticket-printing-failed"));
  }
}

/**
 * Automatically print a ticket after completing the order
 */
export async function autoPrintTicket() {
  const orderStore = useOrderStore();
  const { selectedOrder } = storeToRefs(orderStore);

  LogService.info(
    `Automatically printing tickets for order ${selectedOrder.value?.reference}`
  );
  try {
    if (
      PrinterConfigModule.ticketType === TicketTypes.MiniTicket ||
      POSConfigModule.isVHS
    ) {
      await printTicketsNative();
    } else {
      await printTicketsThruPrintNode();
    }
  } catch (error) {
    LogService.info(
      `Ticket Printing: Error when auto printing tickets`,
      error,
      LogLevel.Error
    );
  }
}

/**
 * Print tickets thru print node
 * Called when printing tickets automatically
 */
export async function printTicketsThruPrintNode() {
  // Replaced with the following line to fix the bug of not printing the ticket when
  // the printer list is not fetched from backend but the selected printer is saved in local storage
  const { selectedPrinterId } = PrinterConfigModule;
  // No printer is selected if no printer was chosen from select printer dropdown in pos settings
  // or if no printer was saved in local storage
  if (!selectedPrinterId) {
    LogService.info("No printer is selected");
    NotificationService.showCustomError(t("error.no-selected-ticket-printer"));
    return;
  }
  const orderStore = useOrderStore();
  const { selectedOrder } = storeToRefs(orderStore);
  if (!selectedOrder.value) {
    throw new Error(
      "No selected order, error printing tickets with print node"
    );
  }
  const orderId = selectedOrder.value.id;
  LogService.info(
    `Ticket Printing: Printing tickets for order ${orderId} using PrintNode`
  );
  try {
    await postOrderPrint({ orderId, printerId: selectedPrinterId });
  } catch (err: any) {
    LogService.info(
      `Ticket Printing: Error when printing thru printNode for order: ${orderId}`,
      err,
      LogLevel.Error
    );
  }
  NotificationService.showCustomSuccess(
    t("completeorder.print-tickets-success")
  );

  // If auto-scan is enabled, we should scan every printed ticket
  if (POSConfigModule.autoScanTickets) {
    // small delay to avoid overlapping notifications
    await new Promise((resolve) => setTimeout(resolve, 500));
    await scanOrderTicketsBulk();
  }
}

/**
 * Print labels from native POS on the selected label printer
 */
export async function printLabels() {
  const printer = PrinterConfigModule.labelPrinter;
  if (!printer) {
    NotificationService.showCustomError(t("error.no-selected-label-printer"));
    return;
  }

  const orderStore = useOrderStore();
  const { selectedOrder, selectedOrderSummary: orderSummary } =
    storeToRefs(orderStore);
  const order = selectedOrder.value;
  if (!order) {
    throw new Error("No selected order");
  }

  const groupId = POSConfigModule.groupId;

  // Build the order-line lookup, then print one label per ACTIVE ticket token.
  const orderLineById = new Map<string, OrderLine>();
  for (const line of orderSummary.value.orderLines) {
    orderLineById.set(line.id, line);
  }
  const activeTokens = (order.tokens ?? []).filter(
    (tk) => tk.status === TokenStatus.ACTIVE
  );

  for (const token of activeTokens) {
    const line = orderLineById.get(token.orderLineId);
    // Only standard tickets get a label (legacy Event/Recurring/Normal →
    // OrderLinePublicType.TICKET). Skip addons that inherit a token, and non-tickets.
    if (
      !line ||
      line.sendTicket === false ||
      line.itemType !== OrderLinePublicType.TICKET
    ) {
      continue;
    }

    const labelInfo: IBookingLabel = {
      date: dayjs(line.startDateTime).format("DD.MM.YYYY"),
      startTime: dayjs(line.startDateTime).format("HH:mm"),
      endTime: dayjs(line.endDateTime).format("HH:mm"),
      ticketLogo: "",
    };
    if (
      groupId &&
      [16383 /* Dev */, 16342 /* Test */, 19672 /* Jumpark */].includes(groupId)
    ) {
      // For Jumpark, we need to print the 'Zone' logo: ticketLogo picture.
      // categoryId is the category UUID; the pictures store is keyed by the
      // numeric id, so resolve it through the product category.
      const [pc] = await getProductCategoriesByUuids([String(line.categoryId)]);
      labelInfo.ticketLogo = pc
        ? (await getLocalPictureBase64(
            SmeetzPictureEntityTypes.TicketLogo,
            pc.id
          )) || ""
        : "";

      printLabel(labelInfo, LabelTypes.Jumpark);
    } else {
      printLabel(labelInfo);
    }
  }

  // If auto-scan is enabled, we should scan every printed ticket
  if (POSConfigModule.autoScanTickets) {
    // small delay to avoid overlapping notifications
    await new Promise((resolve) => setTimeout(resolve, 500));
    await scanOrderTicketsBulk();
  }
}

/**
 * Takes a booking pulled from remote DB using the query GetFullBookingById
 * and returns it in IProductSlotBooking format
 */
export async function toLocalProductSlotBooking(
  order: any
): Promise<IProductSlotBooking> {
  console.log("----- hi from toLocalProductSlotBooking");
  LogService.info(
    `Converting attached order pulled from backend into IPSB format, id ${order.id}`,
    {
      params: order,
    }
  );
  // Note that uuid in all entities nested to the booking is probably null, you need to give it a value and do the relations here
  // (e.g: accounting uuid, pss uuid, booking uuid)
  let bookingUuid = order.uuid;
  if (!bookingUuid) {
    // There is no booking uuid in this order, lets consider its Id as a uuid
    bookingUuid = String(order.id);
  }

  // Before doing any data handling on Booking/Ticket custom fields, we need to keep only custom fields that are set to be displayed in POS
  // Answers under cancelled fields are dropped like the fields themselves:
  // a deleted field is never shown nor considered, even when answered.
  // Handling Booking custom fields first
  order.customFieldsAnswers = order.customFieldsAnswers.filter(
    (c: any) =>
      // Fifth bit should be always on
      (10000 & Number(c.customField.visibleFor)) === 16 &&
      c.customField.cancelled !== 1
  );
  // Handling Ticket custom fields next
  for (let i = 0; i < order.pssList.length; i++) {
    order.pssList[i].customFieldsAnswers = order.pssList[
      i
    ].customFieldsAnswers.filter(
      (c: any) =>
        // Fifth bit should be always on
        (10000 & Number(c.customField.visibleFor)) === 16 &&
        c.customField.cancelled !== 1
    );
  }

  // 0- If the booking or its pss has customFields answered, then we need to pull deserialized answers from backend
  // using an api call
  const ticketsAnswersIds =
    order.pssList
      .map((pss: any) =>
        pss.customFieldsAnswers.map((c: any) => Number(c.uuid))
      )
      .flat() || [];
  const bookingsAnswersIds =
    order.customFieldsAnswers.map((c: any) => Number(c.uuid)) || [];
  const deserializedAnswers: {
    customBookingAnswers: any[];
    customTicketAnswers: any[];
  } = {
    customBookingAnswers: [],
    customTicketAnswers: [],
  };
  if (bookingsAnswersIds.length || ticketsAnswersIds.length) {
    AppModule.updatedIsLoading(true);
    const resp = await getDeserializedAnswers(
      Number(POSConfigModule.selectedConfig?.group),
      ticketsAnswersIds,
      bookingsAnswersIds
    );
    if (resp) {
      deserializedAnswers.customBookingAnswers =
        resp.data?.customBookingAnswers ?? [];
      deserializedAnswers.customTicketAnswers =
        resp.data?.customTicketAnswers ?? [];
    }
    AppModule.updatedIsLoading(false);
  }

  // We're now done with answered booking custom fields, the answered booking custom fields are now attached to the order object
  // What we need to do now is to look for unanswered booking custom fields
  // those will exist if booking is done in B2C or BO and booking custom fields are optional, the user can skip them
  // and when the user skips them our backend is not creating an empty answer for them
  // so what we need to do is to identify them and attach them UNANSWERED to the order object so that when user moves forward to payment step he will see them unanswered and he can answer them or skip them again
  // PS: Unanswered custom fields that are not supposed to be displayed in POS (fifth bit of visibleFor is not on) should not be added to the order object
  // PS2: Cancelled custom fields should not be added to the order object
  console.log(
    "----------- going to check for possible unanswered booking custom fields"
  );
  const unansweredBookingCustomFields: any[] = [];
  const answeredBookingCustomFields = order.customFieldsAnswers || [];
  console.log("----------- answeredBookingCustomFields");
  console.log(answeredBookingCustomFields);
  order.pssList.forEach((p: any) => {
    // for each pss we need to check if it has booking custom fields
    if (
      p.ProductSlotCategoryRead &&
      p.ProductSlotCategoryRead.productRead &&
      p.ProductSlotCategoryRead.productRead.customBookingFieldList &&
      p.ProductSlotCategoryRead.productRead.customBookingFieldList.length
    ) {
      // Lets first filter out cancelled custom fields then we filter out custom fields that are not supposed to be displayed in POS
      p.ProductSlotCategoryRead.productRead.customBookingFieldList
        .filter((notCancelled: any) => notCancelled.cancelled === 0)
        .filter(
          (toBeDisplayedInPOS: any) =>
            (10000 & Number(toBeDisplayedInPOS.visibleFor)) === 16
        )
        .forEach((c: any) => {
          // lets check if this custom field is already answered
          const answeredField = answeredBookingCustomFields.find(
            // custom field is considered answered if it is the exact custom field (using id) that has an answer or if a similar one is answered (similar one meaning having same name same information type and same question type)
            (f: any) =>
              f.customField.id === c.id ||
              (f.customField.questionType === c.questionType &&
                f.customField.informationType === c.informationType &&
                String(f.customField.name).toUpperCase() ===
                  String(c.name).toUpperCase())
          );
          console.log("----------- answeredField");
          console.log(answeredField);
          if (answeredField) {
            console.log(
              "--------- this custom field is already answered, skipping it"
            );
            // this custom field is already answered, lets skip it
            return;
          }
          console.log(
            "---------- this custom field is not answered, adding it to the list of unanswered booking custom fields"
          );
          // this custom field is not answered, lets add it to the list of unanswered booking custom fields but only if it is not already added or a similar one is already added (similar one meaning having same name same information type and same question type)
          const similarField = unansweredBookingCustomFields.find(
            (acf: any) =>
              acf.id === c.id ||
              (acf.questionType === c.questionType &&
                acf.informationType === c.informationType &&
                acf.name.toUpperCase() === c.name.toUpperCase())
          );
          if (!similarField) {
            console.log(
              "----------- so no similar field is already added to unanswered booking custom field, lets add this field now"
            );
            // so no similar field is already added to unanswered booking custom field, lets add this field now
            unansweredBookingCustomFields.push(c);
          }
        });
    }
  });
  console.log("----------- answeredBookingCustomFields");
  console.log(answeredBookingCustomFields);
  console.log("----------- unansweredBookingCustomFields");
  console.log(unansweredBookingCustomFields);
  if (unansweredBookingCustomFields && unansweredBookingCustomFields.length) {
    console.log(
      "----------- so there are unanswered booking custom fields, lets attach them as UNANSWERED into the booking"
    );
    // so there are unanswered booking custom fields, lets attach them as UNANSWERED into the booking
    unansweredBookingCustomFields.forEach((uacf: any) => {
      ///////
      const now = nowDB();

      const answer: ICustomFieldAnswer = {
        uuid: generateID(),
        bookingUuid: bookingUuid,
        answer: "",
        listAnswer: [],
        createdAt: now,
        updatedAt: now,
        customField: {
          id: uacf.id,
          answerExample: uacf.answerExample,
          cancelled: uacf.cancelled,
          informationType: uacf.informationType,
          list: uacf.list,
          name: uacf.name,
          position: uacf.position,
          productId: uacf.productId,
          questionType: uacf.questionType,
          validation: uacf.validation,
          visibleFor: uacf.visibleFor,
        },
      };

      answer.customField.customFieldType = CustomFieldType.BOOKING;
      ///////
      order.customFieldsAnswers.push(answer);
    });
  }
  console.log("----------- done with unanswered booking custom fields");
  console.log(JSON.parse(JSON.stringify(order.customFieldsAnswers)));

  // 1- Convert pssList
  LogService.info(`Converting pssList into local format, order id ${order.id}`);
  const pssList: IProductSlotState[] = [];
  for (let i = 0; i < order.pssList.length; i++) {
    const backendPSS = order.pssList[i];
    // We conider all pss except ABONDONNED ones just like B2B does in order view
    if (order.pssList[i].bookingStatus === BookingStatus.Abandoned) {
      continue;
    }
    const pcType =
      PCTypes[Number(order.pssList[i].ProductSlotCategoryRead.Type)];
    const productId = order.pssList[i].ProductSlotCategoryRead.ProductId;
    let isMemberhsipSeated = false;
    if (
      order.pssList[i].ProductSlotCategoryRead.mainCategory &&
      order.pssList[i].ProductSlotCategoryRead.mainCategory.type ===
        PCTypes.MembershipSeated
    ) {
      isMemberhsipSeated = true;
    }
    // delete order.pssList[i].ProductSlotCategoryRead;

    // Lets add pssType which doesn't exist in backend schema but required in frontend
    const pssType =
      pcType === PCTypes[PCTypes.Retail]
        ? PSSType[PSSType.Retail]
        : PSSType[PSSType.Ticket];

    // If this is of type Retail then we need to assign retail fields values
    let retailOptionId = 0;
    let retailOptionName = "";
    if (pssType === PSSType[PSSType.Retail]) {
      retailOptionId =
        order.pssList[i].ProductSlotPriceRead.RetailOptionList[0].Id;
      retailOptionName =
        order.pssList[i].ProductSlotPriceRead.RetailOptionList[0].OptionName;
    }

    // No need for the field anymore, lets remove it from the object
    delete order.pssList[i].ProductSlotPriceRead;

    // if this pss is an addon we need its mainTicketId
    const mainTicketId = order.pssList[i].mainTicketId;
    delete order.pssList[i].mainTicketId;

    let isAutomaticAddon = false;
    if (mainTicketId) {
      const packagePrice = order.pssList[i].PackagePriceRead;
      isAutomaticAddon =
        packagePrice && packagePrice.AutomaticallyBook === 1 ? true : false;
    }
    if (isAutomaticAddon) {
      let isChillonPackage = false;
      if (order.pssList[i].accountingValue) {
        isChillonPackage = true;
      }
      const mainTicketIndex = order.pssList.findIndex(
        (p: any) => p.id === mainTicketId
      );
      if (mainTicketIndex > -1) {
        // if it was not added to pssList yet then find it in pulled order and edit it and it will be added evemtually
        order.pssList[mainTicketIndex].hasAutomaticAddons = true;
        if (
          !order.pssList[mainTicketIndex].isChillonPackage &&
          isChillonPackage
        ) {
          order.pssList[mainTicketIndex].isChillonPackage = isChillonPackage;
        }
      }
      const mainPssIndex = pssList.findIndex((p: any) => p.id === mainTicketId);
      if (mainPssIndex > -1) {
        pssList[mainPssIndex].hasAutomaticAddons = true;
        if (!pssList[mainPssIndex].isChillonPackage && isChillonPackage) {
          pssList[mainPssIndex].isChillonPackage = isChillonPackage;
        }
      }
    }
    delete order.pssList[i].PackagePriceRead;

    // assign order session id to pss
    const orderSession = order.pssList[i].OrderSessionRead;
    order.pssList[i].orderSessionId = orderSession?.Uid;
    delete order.pssList[i].OrderSessionRead;

    const pss: IProductSlotState = {
      bookingUuid: bookingUuid,
      ...order.pssList[i],
    };

    // convert start / end datetime to POS startDateTime ISO string format
    if (backendPSS.startDateTime && backendPSS.endDateTime) {
      pss.startDateTime = dayjs.utc(backendPSS.startDateTime).toISOString();
      pss.endDateTime = dayjs.utc(backendPSS.endDateTime).toISOString();
    }

    // convert other pss timestamp fields to ISO format
    pss.creationDateTime = dayjs.utc(backendPSS.creationDateTime).toISOString();
    pss.updatedAt = dayjs.utc(backendPSS.updatedAt).toISOString();
    if (backendPSS.refundedAt) {
      pss.refundedAt = dayjs.utc(backendPSS.refundedAt).toISOString();
    }
    if (backendPSS.canceledAt) {
      pss.canceledAt = dayjs.utc(backendPSS.canceledAt).toISOString();
    }
    pss.pssType = pssType;
    pss.productId = productId;

    if (pssType === PSSType[PSSType.Retail]) {
      pss.retailOptionId = retailOptionId;
      pss.retailOptionName = retailOptionName;
    }

    pss.isAutomaticAddon = isAutomaticAddon;
    if (!pss.mainTicketUUID && mainTicketId) {
      pss.mainTicketUUID = String(mainTicketId);
    }

    // if this pss is of type membership seated (17)
    pss.isMemberhsipSeated = isMemberhsipSeated;

    // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
    if (!pss.uuid) {
      pss.uuid = String(pss.id);
    }

    // Lets assign pss.seatType a value
    if (isSeatedPssType(pss.type) && pss.fullSeat && pss.fullSeat.own.type) {
      const seatType = pss.fullSeat.own.type;
      if (seatType === "generalAdmission") {
        pss.seatType = SeatType.GeneralAdmissionArea;
      }
      if (seatType === "seat") {
        pss.seatType = SeatType.Seat;
      }
    }

    //Convert Ticket Custom Fields If Any
    const customFieldsAnswers: ICustomFieldAnswer[] = [];
    // So there are custom fields answers attached to this pss
    for (let j = 0; j < order.pssList[i].customFieldsAnswers.length; j++) {
      let answerString = "";
      let answerList = [];
      const deserialized = deserializedAnswers.customTicketAnswers?.find(
        (c: any) => c.id === order.pssList[i].customFieldsAnswers[j].uuid
      );
      if (deserialized) {
        if (
          order.pssList[i].customFieldsAnswers[j].customField.questionType ===
          QuestionType.LIST /*Array.isArray(deserialized.answer)*/
        ) {
          // So the answer in this case is a list
          // An empty answer must stay an empty list, otherwise a mandatory list
          // looks answered
          answerList = String(deserialized.answer ?? "")
            .split(",")
            .filter(Boolean);
        } else {
          answerString = deserialized.answer ?? "";
        }
      }

      // If the question is a date then we need to convert the answer into a date
      if (
        order.pssList[i].customFieldsAnswers[j].customField.questionType ===
        QuestionType.DATE
      ) {
        const date = answerString ? dayjs(answerString) : null;
        answerString = date?.isValid() ? date.format("YYYY-MM-DD") : "";
      }

      // If the question is a file and it has an aswer then we need to fetch the file from S3 and assign it to fileUrl field
      let fileUrl = "";
      if (
        answerString &&
        order.pssList[i].customFieldsAnswers[j].customField.questionType ===
          QuestionType.FILE
      ) {
        // so this is a file question that has an answer, lets fetch the file from s3
        const resp = await getTicketCustomFieldFile(
          order.id,
          order.pssList[i].customFieldsAnswers[j].uuid
        );
        if (resp && resp.url) {
          fileUrl = resp.url;
        }
      }

      const answer: ICustomFieldAnswer = {
        uuid: String(order.pssList[i].customFieldsAnswers[j].uuid),
        id: order.pssList[i].customFieldsAnswers[j].id,
        pssUuid: pss.uuid,
        answer: answerString,
        fileUrl: fileUrl,
        isCropped: fileUrl ? true : false,
        listAnswer: answerList,
        categoryId: order.pssList[i].categoryId,
        categoryName: order.pssList[i].categoryName,
        priceId: order.pssList[i].priceId,
        priceName: order.pssList[i].priceName,
        createdAt: order.pssList[i].customFieldsAnswers[j].createdAt,
        updatedAt: order.pssList[i].customFieldsAnswers[j].updatedAt,
        customField: {
          id: order.pssList[i].customFieldsAnswers[j].customField.id,
          answerExample:
            order.pssList[i].customFieldsAnswers[j].customField.answerExample,
          cancelled:
            order.pssList[i].customFieldsAnswers[j].customField.cancelled,
          informationType:
            order.pssList[i].customFieldsAnswers[j].customField.informationType,
          list: order.pssList[i].customFieldsAnswers[j].customField.list,
          name: order.pssList[i].customFieldsAnswers[j].customField.name,
          position:
            order.pssList[i].customFieldsAnswers[j].customField.position,
          productId:
            order.pssList[i].customFieldsAnswers[j].customField.productId,
          questionType:
            order.pssList[i].customFieldsAnswers[j].customField.questionType,
          validation:
            order.pssList[i].customFieldsAnswers[j].customField.validation,
          visibleFor:
            order.pssList[i].customFieldsAnswers[j].customField.visibleFor,
        },
      };

      answer.customField.customFieldType = CustomFieldType.TICKET;
      customFieldsAnswers.push(answer);
    }

    /// Now we're done with the answered custom fields
    // lets handle now the unanswered custom fields
    {
      console.log("------------ checking for possible unaswered custom fields");
      console.log("------------ order.pssList[i]");
      console.log(order.pssList[i]);
      // So we have no custom fields answers attached to this pss
      // Lets see if the product of this pss has custom fields so that we attach them UNANSWERED to the pss
      if (
        order.pssList[i].ProductSlotCategoryRead.productRead &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customTicketFieldList &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customTicketFieldList.length
      ) {
        console.log(
          "----------- now handling possible unanswered tickets custom fields"
        );
        // So this PSS product has ticket custom fields attached to it
        // lets make sure that this pss price is mentioned inside custom fields prices
        // if so we will attach the custom field into the pss
        console.log(
          "----------- entering into order.pssList[i].ProductSlotCategoryRead.productRead.customTicketField loop"
        );
        for (
          let l = 0;
          l <
          order.pssList[i].ProductSlotCategoryRead.productRead
            .customTicketFieldList.length;
          l++
        ) {
          // Foreach ticket custom field related to the product of this pss we will make sure it is linked to this pss by checking if the list of prices included in the custom field setup contains the priceId of this pss

          // Lets first check if this custom field is cancelled in BO so that we skip it
          console.log(
            "-------- Lets first check if this custom field is cancelled in BO so that we skip it"
          );
          if (
            order.pssList[i].ProductSlotCategoryRead.productRead
              .customTicketFieldList[l].cancelled === 1
          ) {
            // so this custom field is cancelled, we skip it
            console.log(
              "-------- so this custom field is cancelled, we skip it"
            );
            continue;
          }
          console.log(
            "-----------checking if this custom field is related to this pss thru pricesId"
          );
          console.log("----------- custom field");
          console.log(
            order.pssList[i].ProductSlotCategoryRead.productRead
              .customTicketFieldList[l]
          );
          console.log("----------- pss priceId");
          console.log(order.pssList[i].priceId);
          if (
            order.pssList[
              i
            ].ProductSlotCategoryRead.productRead.customTicketFieldList[
              l
            ].prices
              .map((p: any) => p.id)
              .includes(Number(order.pssList[i].priceId))
          ) {
            console.log(
              "----------- so custom field is related to the pss thru priceId"
            );
            // Now lets check if this custom field is already answered
            // HAMZA HERE: const UnansweredCustomFields = they are the ones mentioned inside pss.psc.product.ticketcustomfields with the price of this pss but not mentioned inside pss.customfields
            // Mirror the booking-level rules over the active fields of the
            // pss (answered or already attached): exact id, or a similar
            // field (same name + questionType + informationType), so twin
            // active fields show and validate only once.
            const currentField =
              order.pssList[i].ProductSlotCategoryRead.productRead
                .customTicketFieldList[l];
            const alreadyHandledField = customFieldsAnswers.find(
              (f: any) =>
                f.customField.id === currentField.id ||
                (f.customField.questionType === currentField.questionType &&
                  f.customField.informationType ===
                    currentField.informationType &&
                  String(f.customField.name).toUpperCase() ===
                    String(currentField.name).toUpperCase())
            );
            if (alreadyHandledField) {
              console.log(
                "----------- yes, this custom field is already answered in the pss, so we will not attach it as unanswered"
              );
              // this custom field is already answered in backend, no need to attach it again as unanswered
              continue;
            }
            console.log(
              "----------- no, this custom field is not answered in the pss, so we will attach it as unanswered"
            );
            // So this custom field has priceId of this pss
            // Lets attach it Unanswered to the pss
            const now = nowDB();

            const answer: ICustomFieldAnswer = {
              uuid: generateID(),
              pssUuid: String(order.pssList[i].uuid),
              priceId: order.pssList[i].priceId,
              priceName: order.pssList[i].priceName,
              categoryId: order.pssList[i].categoryId,
              categoryName: order.pssList[i].categoryName,
              answer: "",
              listAnswer: [],
              createdAt: now,
              updatedAt: now,
              customField:
                order.pssList[i].ProductSlotCategoryRead.productRead
                  .customTicketFieldList[l],
            };

            answer.customField.customFieldType = CustomFieldType.TICKET;
            customFieldsAnswers.push(answer);
          } else {
            console.log(
              "----------- so custom field is NOT related to the pss thru priceId, skipping it"
            );
          }
        }
        console.log(
          "----------- done with order.pssList[i].ProductSlotCategoryRead.productRead.customTicketFieldList loop"
        );
      } else {
        console.log(
          "----------- so there is no tickets custom fields attached to this pss product"
        );
      }
      if (
        order.pssList[i].ProductSlotCategoryRead.productRead &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customBookingFieldList &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customBookingFieldList.length
      ) {
        console.log(
          "----------- now handling possible unanswered booking custom fields"
        );
        // TODO HAMZA: this should be moved out of the pss loop
      }
    }
    ////////////
    // End of ticket custom fields conversion loop

    pss.customFieldsAnswers = customFieldsAnswers;
    pss._discountValue =
      Number(pss._discountValue) - Number(pss.promocodeDiscountValue);

    // Lets check if this pss is a seating ticket, if it is then we will need its eventKey
    // its eventKey is stored in pss.timeslot.eventKey
    if (isSeatedPssType(pss.type)) {
      // TODO Hamza: if the PC is none-reccuring then the eventKey resides in Resources entity not in Timeslots entity
      pss.eventKey = backendPSS.timeslot.eventKey;
    }

    delete order.pssList[i].ProductSlotCategoryRead;
    pssList.push(pss);
  } // End of converting pss list to local format

  ///////
  // 0.5- If the booking or its pss has customFields of type list/dropdown/radio, then we need to pull deserialized lists from backend
  // using an api call
  const customBookingListFields = order.customFieldsAnswers
    .map((c: any) => c.customField)
    .filter(
      (cust: any) =>
        cust?.questionType === QuestionType.DROPDOWN ||
        cust?.questionType === QuestionType.RADIO ||
        cust?.questionType === QuestionType.LIST
    );
  const customTicketListFields = pssList
    .map((p: any) => p.customFieldsAnswers)
    .flat()
    .map((c: any) => c.customField)
    .flat()
    .filter(
      (cust: any) =>
        cust?.questionType === QuestionType.DROPDOWN ||
        cust?.questionType === QuestionType.RADIO ||
        cust?.questionType === QuestionType.LIST
    );
  if (customBookingListFields.length || customTicketListFields.length) {
    // So we have custom fields of type radio/dropdown/list, lets fetch deserialzed 'list' values
    const customBookingFieldsIds = customBookingListFields.map((c: any) =>
      Number(c?.id)
    );
    const customTicketFieldsIds = customTicketListFields.map((c: any) =>
      Number(c?.id)
    );
    const resp = await getDeserializedCustomFieldQuestions(
      Number(POSConfigModule.selectedConfig?.group),
      customTicketFieldsIds,
      customBookingFieldsIds
    );
    const deserializedCustomBookingFields = resp.data.customBookingFields;
    const deserializedCustomTicketFields = resp.data.customTicketFields;

    // Lets update list of BOOKING fields
    if (
      deserializedCustomBookingFields &&
      deserializedCustomBookingFields.length
    ) {
      for (let i = 0; i < deserializedCustomBookingFields.length; i++) {
        // // lets look for the custom field and change the 'list' property value
        for (let j = 0; j < order.customFieldsAnswers.length; j++) {
          if (
            deserializedCustomBookingFields[i].id ===
            order.customFieldsAnswers[j].customField.id
          ) {
            order.customFieldsAnswers[j].customField.list =
              deserializedCustomBookingFields[i].list;
          }
        }
      }
    }

    // lets proceed for TICKET custom fields
    if (
      deserializedCustomTicketFields &&
      deserializedCustomTicketFields.length
    ) {
      console.log("------------ handling deserializedCustomTicketFields");
      console.log("------------ deserializedCustomTicketFields");
      console.log(deserializedCustomTicketFields);
      // looping thru deserialized custom fields
      for (let i = 0; i < deserializedCustomTicketFields.length; i++) {
        // // lets look for the custom field in the pssList and change the custom field 'list' property value
        // Looping thru pss list
        for (let j = 0; j < pssList.length /*order.pssList.length*/; j++) {
          // looping thru each pss custom fields
          const pssCustomFieldsAnswers = pssList[j].customFieldsAnswers;
          if (pssCustomFieldsAnswers && pssCustomFieldsAnswers) {
            for (let h = 0; h < pssCustomFieldsAnswers.length; h++) {
              if (
                deserializedCustomTicketFields[i].id ===
                pssCustomFieldsAnswers[h].customField.id
              ) {
                pssCustomFieldsAnswers[h].customField.list =
                  deserializedCustomTicketFields[i].list;
              }
            }
          }
        }
      }
    } else {
      console.log("---------- there is no desierialized customTicketFields");
    }
  }
  ///////

  // 2- Convert accountings, and foreach accounting convert its accEntries (do the relations to pss)
  LogService.info(
    `Converting accountings into local format, order id ${order.id}`
  );
  const accountings: IAccounting[] = [];
  const originalAccountings: any[] = [];
  // Lets avoid touching order.accountings by creating another array to be used in conversion process
  for (let i = 0; i < order.accountings.length; i++) {
    originalAccountings.push(order.accountings[i]);
  }

  for (let i = 0; i < originalAccountings.length; i++) {
    // assign order session id to accounting
    const orderSession = originalAccountings[i].OrderSessionRead;
    originalAccountings[i].orderSessionId = orderSession?.Uid;
    delete originalAccountings[i].OrderSessionRead;

    const acc: IAccounting = {
      bookingUuid: bookingUuid,
      ...cloneDeep(originalAccountings[i]),
    };

    if (
      acc.paymentMethod === PaymentMethods.METHOD_PENDING &&
      acc.state === AccountingState.Pending
    ) {
      // Ignore pending accountings as they are not considered in balance.
      continue;
    }
    // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
    if (!acc.uuid) {
      acc.uuid = String(acc.id);
      acc._id = String(acc.id);
    }

    if (acc.promoCodeId) {
      acc.promocode = originalAccountings[i].PromoCodeRead.code || "";
    }

    // This is used by backend to store externalVoucher code
    if (
      acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
      originalAccountings[i].note
    ) {
      acc.note = originalAccountings[i].note;
      acc.promocode = originalAccountings[i].note;
    }
    // convert accounting timestamp fields to ISO format
    acc.createdAt = dayjs.utc(originalAccountings[i].createdAt).toISOString();
    acc.updatedAt = dayjs.utc(originalAccountings[i].updatedAt).toISOString();

    // lets get rid of cloned accEntries attached to the accounting object because we will create our own accEntries
    acc.accountingEntriesList = [];
    for (
      let j = 0;
      j < originalAccountings[i].accountingEntriesList.length;
      j++
    ) {
      // assign order session id to accounting entry
      const orderSession =
        originalAccountings[i].accountingEntriesList[j].OrderSessionRead;
      originalAccountings[i].accountingEntriesList[j].orderSessionId =
        orderSession?.Uid;
      delete originalAccountings[i].accountingEntriesList[j].OrderSessionRead;

      let pssUuid =
        originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
          .Uuid;
      // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
      if (!pssUuid) {
        pssUuid = String(
          originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
            .Id
        );
      }

      // assign offer id and uid to accounting entries
      const offer = originalAccountings[i].accountingEntriesList[j].OfferRead;
      if (offer) {
        originalAccountings[i].accountingEntriesList[j].offerId = offer.Id;
        originalAccountings[i].accountingEntriesList[j].offerUid = offer.Uid;
        delete originalAccountings[i].accountingEntriesList[j].OfferRead;
      }

      // Lets remove ProductSlotStateRead field from the accounting entry
      delete originalAccountings[i].accountingEntriesList[j]
        .ProductSlotStateRead;

      const accEntry: IAccountingEntry = {
        accountingUuid: acc.uuid,
        productSlotStateUuid: pssUuid,
        ...originalAccountings[i].accountingEntriesList[j],
      };
      // If the acEntry does not have a uuid then we need to use its id as a uuid for it
      if (!accEntry.uuid) {
        accEntry.uuid = String(accEntry.id);
        accEntry._id = String(accEntry.id);
      }

      // convert accounting entry timestamp fields to ISO format
      accEntry.createdAt = dayjs
        .utc(originalAccountings[i].accountingEntriesList[j].createdAt)
        .toISOString();
      accEntry.updatedAt = dayjs
        .utc(originalAccountings[i].accountingEntriesList[j].updatedAt)
        .toISOString();

      acc.accountingEntriesList.push(accEntry);
    }

    accountings.push(acc);
  }

  // Now lets assign acc.originalAccountingUuid a value if it exists because is used when doing refunds
  for (let i = 0; i < originalAccountings.length; i++) {
    const backendAccounting = originalAccountings[i];
    if (backendAccounting.originalAccountingId) {
      // Lets find the accounting with this Id
      const newAcc = accountings.find(
        (a) => a.id === backendAccounting.originalAccountingId
      );
      if (newAcc) {
        // If we found it then lets assign its uuid to originalAccountingUuid field in the accountings array
        const convertedAccounting = accountings.find(
          (acc) => acc.id === backendAccounting.id
        );
        if (convertedAccounting) {
          convertedAccounting.originalAccountingUuid = newAcc.uuid;
        }
      }
    }
  }

  let sellerId = order.sellerId;
  if (!order.sellerId) {
    sellerId = UserModule.userId;
  }

  let cashfloatUuid = "";
  if (CashFloatModule.cashFloat) {
    cashfloatUuid = CashFloatModule.cashFloat.uid;
  }

  // 3- Convert Booking Custom Fields If Any
  const customFieldsAnswers: ICustomFieldAnswer[] = [];
  for (let i = 0; i < order.customFieldsAnswers.length; i++) {
    let answerString = "";
    let answerList = [];
    const deserialized = deserializedAnswers.customBookingAnswers?.find(
      (c: any) => c.id === order.customFieldsAnswers[i].uuid
    );
    if (deserialized) {
      if (
        order.customFieldsAnswers[i].customField.questionType ===
        QuestionType.LIST /*Array.isArray(deserialized.answer)*/
      ) {
        // So the answer in this case is a list
        // An empty answer must stay an empty list, otherwise a mandatory list
        // looks answered
        answerList = String(deserialized.answer ?? "")
          .split(",")
          .filter(Boolean);
      } else {
        answerString = deserialized.answer ?? "";
      }
    }

    // If the question is a date then we need to convert the answer into a date
    if (
      order.customFieldsAnswers[i].customField.questionType ===
      QuestionType.DATE
    ) {
      const date = answerString ? dayjs(answerString) : null;
      answerString = date?.isValid() ? date.format("YYYY-MM-DD") : "";
    }

    // If the question is a file and it has an aswer then we need to fetch the file from S3 and assign it to fileUrl field
    let fileUrl = "";
    if (
      answerString &&
      order.customFieldsAnswers[i].customField.questionType ===
        QuestionType.FILE
    ) {
      // so this is a file question that has an answer, lets fetch the file from s3
      const resp = await getBookingCustomFieldFile(
        order.id,
        order.customFieldsAnswers[i].uuid
      );
      if (resp && resp.url) {
        fileUrl = resp.url;
      }
    }

    const answer: ICustomFieldAnswer = {
      uuid: String(order.customFieldsAnswers[i].uuid),
      id: order.customFieldsAnswers[i].id,
      bookingUuid: bookingUuid,
      answer: answerString,
      fileUrl: fileUrl,
      isCropped: fileUrl ? true : false,
      listAnswer: answerList,
      createdAt: order.customFieldsAnswers[i].createdAt,
      updatedAt: order.customFieldsAnswers[i].updatedAt,
      customField: {
        id: order.customFieldsAnswers[i].customField.id,
        answerExample: order.customFieldsAnswers[i].customField.answerExample,
        cancelled: order.customFieldsAnswers[i].customField.cancelled,
        informationType:
          order.customFieldsAnswers[i].customField.informationType,
        list: order.customFieldsAnswers[i].customField.list,
        name: order.customFieldsAnswers[i].customField.name,
        position: order.customFieldsAnswers[i].customField.position,
        productId: order.customFieldsAnswers[i].customField.productId,
        questionType: order.customFieldsAnswers[i].customField.questionType,
        validation: order.customFieldsAnswers[i].customField.validation,
        visibleFor: order.customFieldsAnswers[i].customField.visibleFor,
      },
    };

    answer.customField.customFieldType = CustomFieldType.BOOKING;
    customFieldsAnswers.push(answer);
  }
  // add current pos config to the converted booking
  let posConfigId: number | undefined;
  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    posConfigId = getPOSConfigId();
  }
  const groupId = Number(POSConfigModule.groupInfo?.id);

  // 5- find offer applications and construct appliedOffers object for the pulled booking
  const offerApplications: IOfferApplication[] = [];
  for (let i = 0; i < accountings.length; i++) {
    const accounting = accountings[i];
    if (
      accounting.transactionType !==
        TransactionTypes.TRANSACTION_TYPE_DISCOUNT ||
      (accounting.paymentMethod !== PaymentMethods.METHOD_POS_OFFER &&
        accounting.paymentMethod !== PaymentMethods.METHOD_OFFER) ||
      accounting.refund ||
      accounting.state !== AccountingState.Completed
    ) {
      continue;
    }
    const aeWithOffer = accounting.accountingEntriesList.find(
      (ae) => !!ae.offerId
    );
    if (!aeWithOffer?.offerId || !aeWithOffer?.offerUid) {
      continue;
    }
    const offerApplication: IOfferApplication = {
      applicationUuid: accounting.uuid as string,
      offerId: aeWithOffer.offerId,
      offerUid: aeWithOffer.offerUid,
      code: accounting.promocode,
      codeId: accounting.promoCodeId || undefined,
      applicationDateTime: String(accounting.createdAt),
      isOfferConfirmedOnBackend: true,
      isOfferStillEligible: true,
      applicationDetails: [],
    };

    const applicationDetails: IOfferApplicationDetails[] = [];
    for (let i = 0; i < accounting.accountingEntriesList.length; i++) {
      const ae = accounting.accountingEntriesList[i];
      const pss = pssList.find((pss) => pss.uuid === ae.productSlotStateUuid);
      if (!pss) {
        continue;
      }
      const productCategory = await getProductCategoryById(pss.categoryId);
      if (!productCategory) {
        continue;
      }
      const { priceId, categoryId, productId } = pss;
      // if an application detail already exists, we increase its items count
      const exisitingApplicationDetail = applicationDetails.find(
        (detail) =>
          detail.priceId === priceId &&
          detail.discountValuePerItem === ae.value / 100
      );
      if (exisitingApplicationDetail) {
        exisitingApplicationDetail.discountItemsCount++;
        continue;
      }
      // otherwise create a new one
      const applicationDetail: IOfferApplicationDetails = {
        priceId,
        categoryId,
        productId,
        ticketGroupId: productCategory.ticketGroups,
        discountValuePerItem: ae.value / 100,
        discountItemsCount: 1,
      };
      applicationDetails.push(applicationDetail);
    }

    offerApplication.applicationDetails = applicationDetails;
    offerApplications.push(offerApplication);
  }
  // OffersModule.setAppliedOffers({ bookingUuid, offerApplications });

  // 6- convert order sessions
  const orderSessions: BaseOrderSession[] = [];
  for (let i = 0; i < order.orderSessions.length; i++) {
    const originalOrderSession = order.orderSessions[i];
    const orderSession: BaseOrderSession = {
      id: originalOrderSession.Uid,
      createdAt: dayjs.utc(originalOrderSession.CreatedAt).toISOString(),
      updatedAt: dayjs.utc(originalOrderSession.UpdatedAt).toISOString(),
      completionDate: originalOrderSession.CompletionDate
        ? dayjs.utc(originalOrderSession.CompletionDate).toISOString()
        : undefined,
      orderId: originalOrderSession.Uid,
      reference: originalOrderSession.Reference,
      bookingUuid,
      currency: order.currency,
      state: originalOrderSession.State,
      groupId,
      channel: originalOrderSession.Channel,
      sessionStart: dayjs.utc(originalOrderSession.CreatedAt).toISOString(),
      seqNumber: originalOrderSession.seqNumber,
      // the team agreed that receiptCounter value will be stored in orderSession.reference althouth orderSession.reference has other usages in fiscal branches
      receiptCounterValue:
        originalOrderSession.seqNumber && originalOrderSession.seqNumber > 0
          ? originalOrderSession.receiptCounter
          : "",
    };
    orderSessions.push(orderSession);
  }

  // 7- Create PSB object and return it
  const booking: IProductSlotBooking = {
    _id: String(order.id),
    id: order.id,
    uuid: bookingUuid,
    reference: order.reference,
    state: order.state,
    languageId: order.languageId,
    sellerId: sellerId,
    userId: order.userId || null,
    customerId: order.customerId,
    email: order.email,
    firstName: order.firstName,
    lastName: order.lastName,
    currency: order.currency,
    insertDateTime: dayjs.utc(order.insertDateTime).toISOString(),
    updatedAt: dayjs.utc(order.updatedAt).toISOString(),
    message: order.message,
    pssList: pssList,
    accountings: accountings,
    accountingEntries: [],
    cashfloatUuid: cashfloatUuid,
    customFieldsAnswers: customFieldsAnswers,
    firstPosConfigId: posConfigId,
    groupId: groupId,
    orderSessions,
  };

  if (order.type) {
    booking.type = order.type;
  }

  LogService.info(`Done converting pulled order, order id ${order.id}`, {
    params: booking,
  });
  return booking;
}

/**
 * @returns an array of ITeamSetting related the PCs of the current booking
 */
export async function getBookingTeamsSetings(): Promise<ITeamSetting[]> {
  const pssList = BookingModule.selectedBooking?.pssList || [];
  // let's gather all the PC ids of the tickets
  const PCIds: number[] = [];
  for (let i = 0; i < pssList.length; i++) {
    if (
      pssList[i].bookingStatus === BookingStatus.Canceled ||
      pssList[i].bookingStatus === BookingStatus.Abandoned ||
      pssList[i].pssType === PSSType[PSSType.Retail] ||
      PCIds.includes(pssList[i].categoryId)
    ) {
      continue;
    }
    PCIds.push(pssList[i].categoryId);
  }
  const PCs = await getProductCategoriesByIds(PCIds);
  const teamSettings: ITeamSetting[] = [];
  for (let i = 0; i < PCs.length; i++) {
    if (PCs[i].teamSetting) {
      teamSettings.push(PCs[i].teamSetting as ITeamSetting);
    }
  }
  return teamSettings;
}

/**
 * @returns tickets (IQuantifiedProductSlotState) grouped by category and timeslot.
 */
function groupedTeamsTickets(
  pssList: IProductSlotState[]
): IQuantifiedProductSlotState[] {
  const groupedPSSList: IQuantifiedProductSlotState[] = [];
  for (let i = 0; i < pssList.length; i++) {
    if (
      pssList[i].pssType === PSSType[PSSType.Retail] ||
      pssList[i].bookingStatus === BookingStatus.Canceled ||
      pssList[i].bookingStatus === BookingStatus.Abandoned
    ) {
      // we only consider non cancelled tickets
      continue;
    }
    // pss are grouped by category and timeslot
    let groupedPSS = groupedPSSList.find(
      (pss) =>
        pss.categoryId === pssList[i].categoryId &&
        pss.timeSlotId === pssList[i].timeSlotId
    );
    if (!groupedPSS) {
      groupedPSS = Object.assign(
        {
          quantity: 1,
          pssIds: [String(pssList[i].uuid)],
        },
        pssList[i]
      );
      groupedPSSList.push(groupedPSS);
    } else {
      groupedPSS.quantity = groupedPSS.quantity + 1;
      groupedPSS.pssIds.push(String(pssList[i].uuid));
    }
  }
  return groupedPSSList;
}

/**
 * this function creates teams object for the current booking.
 * @returns array of ITeam
 */
export async function createTeams(): Promise<ITeam[]> {
  // we need all the team settings related to this booking
  const settings = await getBookingTeamsSetings();
  if (!settings) {
    // no settings means this booking doesn't require teams.
    return [];
  }
  const countedPricesIds = settings.map((s) => s.countedPsp).flat();
  const pssList = BookingModule.selectedBooking?.pssList || [];
  // teams are grouped by category and timeslots
  const tickets = groupedTeamsTickets(
    pssList.filter((pss) => countedPricesIds.includes(pss.priceId))
  );

  const teams: ITeam[] = [];
  for (let i = 0; i < tickets.length; i++) {
    // category team setting
    const categoryTeamSettings = settings.find(
      (s) => s.categoryId === tickets[i].categoryId
    );
    if (!categoryTeamSettings) continue;
    // calculate the number of teams needed
    const numberOfTeams = Math.ceil(
      tickets[i].quantity / categoryTeamSettings.maxParticipants
    );
    let remainingQuantity = tickets[i].quantity;

    for (let j = 1; j <= numberOfTeams; j++) {
      // calculate players count for the current team
      // calculate players count for the current team
      const playersCount =
        j === numberOfTeams
          ? remainingQuantity
          : categoryTeamSettings.maxParticipants;
      // create a team with the current set of tickets
      teams.push({
        name: "",
        roomPlayers: playersCount,
        startDateTime: dayjs(tickets[i].startDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        endDateTime: dayjs(tickets[i].endDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        nfcTag: "",
        paired: false,
        ticketUuids: tickets[i].pssIds.slice(
          tickets[i].quantity - remainingQuantity,
          tickets[i].quantity - remainingQuantity + playersCount
        ),
        teamLevel: TeamLevel.MEDIUM,
      });
      remainingQuantity -= playersCount;
    }
  }
  return teams;
}

/**
 * Assemble the @smeetz/receipt input for the order's receipt; a failure prints
 * the legacy template only (the package path is strictly additive).
 */
function safeBuildReceiptInput(
  order: OrderJson,
  receiptInfo: IBookingReceipt
): ReceiptInput | undefined {
  try {
    return buildOrderReceiptInput(order, receiptInfo);
  } catch (e) {
    // ERROR level on purpose: the most likely cause is mandatory fiscal data
    // missing, which must be visible in Graylog rather than buried at info.
    LogService.info(
      "Receipt: failed to build ReceiptInput, printing legacy only",
      { e },
      LogLevel.Error
    );
    return undefined;
  }
}

/**
 * Print the selected order's receipt (or pre-payment bill).
 *
 * Mirrors the prod flow: build the base receipt info from the order, short-circuit
 * for bills (no payments / no signature), stamp the receipt counter, apply country
 * compliance (currently a deferred unsigned stub — tracker P1), print, and mark
 * the counter as printed.
 *
 * @param options `isBill` prints a pre-payment bill instead of a receipt
 */
export async function printBookingReceipt(
  options: { isBill?: boolean } = {}
): Promise<void> {
  const orderStore = useOrderStore();
  const { selectedOrder, selectedOrderSummary } = storeToRefs(orderStore);
  const order = selectedOrder.value;
  if (!order) {
    LogService.info("No selected order in store, aborting receipt printing");
    return;
  }

  const receiptInfo = await buildBaseReceiptInfo(order, options);

  if (options.isBill) {
    receiptInfo.isSignatureRequired = false;
    LogService.info(`Printing bill for order ${receiptInfo.bookingRef}`);
    printReceipt(receiptInfo, safeBuildReceiptInput(order, receiptInfo));
    NotificationService.showCustomSuccess(
      t("completeorder.print-bill-success")
    );
    return;
  }

  await applyReceiptCounter(receiptInfo, order);

  LogService.info(`Printing receipt ${receiptInfo.bookingRef}`, {
    params: receiptInfo,
  });

  const { shouldAbort } = await applyCountryCompliance(receiptInfo, order);
  if (shouldAbort) {
    return;
  }

  // Built AFTER applyCountryCompliance so the France fiscal block (incl. the
  // EFR-signed timestamps) is on receiptInfo. A build failure prints legacy.
  printReceipt(receiptInfo, safeBuildReceiptInput(order, receiptInfo));
  NotificationService.showCustomSuccess(
    t("completeorder.print-receipts-success")
  );

  if (ReceiptModule.receiptCounter) {
    LogService.info(
      `Setting receipt of ${receiptInfo.bookingRef} with counter value ${receiptInfo.orderSessionReference} as Printed`,
      { params: receiptInfo }
    );
    ReceiptModule.setReceiptAsPrinted();
  }
}

export const bookings = computed(() => {
  return Object.values(BookingModule.bookings);
});

export const maxBookingTabs = computed(() => {
  return POSConfigModule.maxBookingTabs;
});

export const maxBookingTabsToShow = 4;
